import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface ContentBlock {
  type: 'paragraph' | 'heading' | 'subheading' | 'image' | 'html' | 'quote';
  content: string;
}

export interface Post {
  id: string;
  title: string;
  subtitle: string;
  categories: string[];
  image: string;
  bodyHtml: string;
  content: ContentBlock[];
  readTime: string;
  createdAt: string;
  featured: boolean;
  published: boolean;
  slug: string;
}

function mapRow(row: Record<string, unknown>): Post {
  return {
    id: row.id as string,
    title: (row.title as string) ?? '',
    subtitle: (row.subtitle as string) ?? '',
    categories: Array.isArray(row.categories) ? (row.categories as string[]) : [],
    image: (row.image as string) ?? '',
    bodyHtml: (row.body_html as string) ?? '',
    content: Array.isArray(row.content) ? (row.content as ContentBlock[]) : [],
    readTime: (row.read_time as string) ?? '5 min de leitura',
    createdAt: (row.created_at as string) ?? new Date().toISOString(),
    featured: Boolean(row.featured),
    published: Boolean(row.published),
    slug: (row.slug as string) ?? '',
  };
}

export function usePosts() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (err) throw err;
      setPosts((data ?? []).map(mapRow));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar posts');
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const addPost = async (post: Post): Promise<Post | null> => {
    try {
      const { data, error: err } = await supabase
        .from('posts')
        .insert({
          title: post.title,
          subtitle: post.subtitle,
          categories: post.categories,
          image: post.image,
          body_html: post.bodyHtml,
          content: post.content,
          read_time: post.readTime,
          featured: post.featured,
          published: post.published,
          slug: post.slug,
        })
        .select()
        .single();

      if (err) throw err;
      const newPost = mapRow(data);
      setPosts(prev => [newPost, ...prev]);
      return newPost;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao criar post');
      return null;
    }
  };

  const updatePost = async (id: string, updates: Partial<Post>): Promise<boolean> => {
    try {
      const dbUpdates: Record<string, unknown> = {};
      if (updates.title !== undefined) dbUpdates.title = updates.title;
      if (updates.subtitle !== undefined) dbUpdates.subtitle = updates.subtitle;
      if (updates.categories !== undefined) dbUpdates.categories = updates.categories;
      if (updates.image !== undefined) dbUpdates.image = updates.image;
      if (updates.bodyHtml !== undefined) dbUpdates.body_html = updates.bodyHtml;
      if (updates.content !== undefined) dbUpdates.content = updates.content;
      if (updates.readTime !== undefined) dbUpdates.read_time = updates.readTime;
      if (updates.featured !== undefined) dbUpdates.featured = updates.featured;
      if (updates.published !== undefined) dbUpdates.published = updates.published;
      if (updates.slug !== undefined) dbUpdates.slug = updates.slug;

      const { error: err } = await supabase
        .from('posts')
        .update(dbUpdates)
        .eq('id', id);

      if (err) throw err;
      setPosts(prev => prev.map(p => (p.id === id ? { ...p, ...updates } : p)));
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao atualizar post');
      return false;
    }
  };

  const deletePost = async (id: string): Promise<boolean> => {
    try {
      const { error: err } = await supabase
        .from('posts')
        .delete()
        .eq('id', id);

      if (err) throw err;
      setPosts(prev => prev.filter(p => p.id !== id));
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao excluir post');
      return false;
    }
  };

  const getFeaturedPosts   = () => posts.filter(p => p.featured && p.published);
  const getPublishedPosts  = () => posts.filter(p => p.published);
  const getPostBySlug      = (slug: string) => posts.find(p => p.slug === slug);
  const getPostsByCategory = (cat: string) =>
    posts.filter(p => p.published && p.categories.includes(cat));

  return {
    posts,
    loading,
    error,
    fetchPosts,
    addPost,
    updatePost,
    deletePost,
    getFeaturedPosts,
    getPublishedPosts,
    getPostBySlug,
    getPostsByCategory,
  };
}